import { Card, Form, FormButtonRow, IconButton } from '@ksyos/design-system';
import { difference } from 'lodash';
import { useEffect, useState } from 'react';
import type { ValidationErrors } from '@frontend/utils/';
import { ManagerAuthorization, ServiceCode, ServiceType } from '@common/enums/';
import { serviceProviderPersonalSettingsEditSchema } from '@common/validation/serviceProvider';
import { fetchJson } from '@frontend/store/fetch';
import { translateKey } from '@frontend/translate';
import {
    emptyValidationErrors,
    hasOneAuthorization,
    updateValidationErrorsForOne,
    useAbortableFetch,
    validate,
} from '@frontend/utils/';
import type { IPreferredServiceOrganizationConsumer } from '../../../interfaces/preferredServiceOrganizationConsumer';
import type { IServiceOrganizationConsumer } from '../../../interfaces/serviceOrganizationConsumer';
import type { IServiceProvider } from '../../../interfaces/serviceProvider';
import { FormContextProvider } from '../../../elements/formContext';
import { CancelButton } from '../../../userDataManagement/CancelButton';
import { SaveButton } from '../../../userDataManagement/SaveButton';
import { AnalysisSettings } from './AnalysisSettings';
import { DateSettings } from './DateSettings';
import { ExaminationAndGradingSettings } from './ExaminationAndGradingSettings';
import { ReceiveRequestSettings } from './ReceiveRequestSettings';
import { RightRequestSettings } from './RightRequestSettings';
import { SelectConsumerSettings } from './SelectConsumerSettings';
import { SoftwareSettings } from './SoftwareSettings';
import { TurnRightOffLink } from './TurnRightOffLink';

interface IPersonalSettingsCarePathServiceProviderSettingProps {
    employmentId: string;
    organizationId: string;
    reloadServiceProviderRight: () => Promise<void>;
    serviceProviderRight: IServiceProvider;
}

export type PersonalSettingsCarePathServiceProviderFormMode = 'read' | 'edit';

export interface IPersonalSettingsCarePathServiceProviderSettingFormSectionProps extends IServiceProvider {
    errors: ValidationErrors;
    handleChange: (key: keyof IServiceProvider, value: IServiceProvider[keyof IServiceProvider]) => void;
    mode: PersonalSettingsCarePathServiceProviderFormMode;
    validateOne: (key: keyof IServiceProvider) => void;
}

export function PersonalSettingsCarePathServiceProviderSetting(
    props: IPersonalSettingsCarePathServiceProviderSettingProps
) {
    const abortableFetch = useAbortableFetch();

    const [isSaving, setIsSaving] = useState(false);
    const [mode, setMode] = useState<PersonalSettingsCarePathServiceProviderFormMode>('read');
    const [
        personalSettingsCarePathServiceProviderSettingEditVersion,
        setPersonalSettingsCarePathServiceProviderSettingEditVersion,
    ] = useState<IServiceProvider | null>(null);
    const [errors, setErrors] = useState<ValidationErrors>(emptyValidationErrors);
    const [preferredOrganizationConsumers, setPreferredOrganizationConsumers] = useState<
        IServiceOrganizationConsumer[]
    >([]);
    const [originalPreferredOrganizations, setOriginalPreferredOrganizations] = useState<
        IPreferredServiceOrganizationConsumer[]
    >([]);
    const [noPreferredServiceConsumerSelected, setNoPreferredServiceConsumerSelected] = useState<boolean>(false);
    const canEdit = hasOneAuthorization(ManagerAuthorization.employmentSettingsEdit);

    if (
        !props.serviceProviderRight.service.serviceType ||
        ![
            ServiceType.examination,
            ServiceType.consultation,
            ServiceType.referral,
            ServiceType.referralSmall,
            ServiceType.contact,
        ].includes(props.serviceProviderRight.service.serviceType)
    ) {
        return null;
    }

    useEffect(() => {
        if (mode === 'edit') {
            setPersonalSettingsCarePathServiceProviderSettingEditVersion({ ...props.serviceProviderRight });
            setNoPreferredServiceConsumerSelected(false);
            loadPreferredOrganizationsWithPractitioners();
        } else {
            setPersonalSettingsCarePathServiceProviderSettingEditVersion(null);
            setErrors(emptyValidationErrors);
        }
    }, [mode, props.serviceProviderRight]);

    return (
        <FormContextProvider
            value={{
                placeholderGroupName: 'serviceProviderSetting',
                item: personalSettingsCarePathServiceProviderSettingEditVersion || props.serviceProviderRight,
                errors: emptyValidationErrors,
                onChange: async (key, value) => handleChange(key, value),
                onDoValidate: async (key) => validateOne(key),
            }}
        >
            <Card>
                <Card.Transition type={mode} />

                {canEdit && (
                    <Card.Header
                        action={
                            showEditIcon() ? (
                                <IconButton
                                    icon="edit-light"
                                    labelText="edit"
                                    onClick={() => setMode('edit')}
                                />
                            ) : undefined
                        }
                        title={`${translateKey('addCarePathSectionTitles.serviceProviderSettings')} ${
                            props.serviceProviderRight.service.name
                        }`}
                    />
                )}
                {!canEdit && (
                    <Card.Header
                        title={`${translateKey('addCarePathSectionTitles.serviceProviderSettings')} ${
                            props.serviceProviderRight.service.name
                        }`}
                    />
                )}
                <Card.Body>
                    <Form submitOnEnter={false}>
                        <RightRequestSettings
                            isAutoCreated={props.serviceProviderRight.isAutoCreated}
                            isEditMode={mode === 'edit'}
                            onChange={handleChange}
                            setToRequestedRightDateTime={props.serviceProviderRight.setToRequestedRightDateTime}
                        />
                        <DateSettings serviceRight={props.serviceProviderRight} />
                        <ExaminationAndGradingSettings {...getPropsForFormSection()} />
                        <AnalysisSettings {...getPropsForFormSection()} />
                        <SelectConsumerSettings
                            {...getPropsForFormSection()}
                            employmentId={props.employmentId}
                            noPreferredServiceConsumerSelected={noPreferredServiceConsumerSelected}
                            originalPreferredOrganizationConsumers={originalPreferredOrganizations}
                            preferredOrganizationConsumers={preferredOrganizationConsumers}
                            setNoPreferredServiceConsumerSelected={setNoPreferredServiceConsumerSelected}
                            setPreferredOrganizationConsumers={setPreferredOrganizationConsumers}
                        />
                        <ReceiveRequestSettings {...getPropsForFormSection()} />
                        <SoftwareSettings
                            {...getPropsForFormSection()}
                            serviceRightType={'ServiceProvider'}
                        />

                        {mode === 'edit' && (
                            <FormButtonRow>
                                <CancelButton
                                    disabled={isSaving}
                                    onCancel={async () => setMode('read')}
                                />
                                <SaveButton
                                    disabled={isSaving}
                                    onSave={handleSubmit}
                                />
                            </FormButtonRow>
                        )}
                    </Form>
                </Card.Body>
                {!props.serviceProviderRight.endDate && canEdit && (
                    <Card.Footer>
                        <TurnRightOffLink
                            organizationId={props.organizationId}
                            reloadServiceRights={async () => {
                                await props.reloadServiceProviderRight();
                                setMode('read');
                            }}
                            serviceRight={props.serviceProviderRight}
                            serviceRightType={'ServiceProvider'}
                        />
                    </Card.Footer>
                )}
            </Card>
        </FormContextProvider>
    );

    async function loadPreferredOrganizationsWithPractitioners() {
        const preferredOrganizations = await getPreferredOrganizations();
        const preferredOrganizationConsumer = await getPreferredOrganizationConsumers(preferredOrganizations);
        setPreferredOrganizationConsumers(preferredOrganizationConsumer);
        setOriginalPreferredOrganizations(preferredOrganizationConsumer);
        if (preferredOrganizations.length > 0) {
            setNoPreferredServiceConsumerSelected(false);
        }
    }

    async function getPreferredOrganizations() {
        const result = await abortableFetch.fetchJson(
            `/api/preferredServiceOrganizationConsumers?isActive=true&serviceProviderId=${props.serviceProviderRight.id}`,
            'GET'
        );

        return result.json.items as IPreferredServiceOrganizationConsumer[];
    }

    async function getPreferredOrganizationConsumers(preferredOrganizations: IPreferredServiceOrganizationConsumer[]) {
        return await Promise.all(
            preferredOrganizations.map(async (preferredOrganization) => {
                const serviceConsumersUrl = `/api/serviceConsumers?organizationId=${preferredOrganization.organization.id}&serviceId=${props.serviceProviderRight.service.id}&isActive=true`;

                const serviceConsumers = (await abortableFetch.fetchJson(serviceConsumersUrl, 'GET')).json.items;

                return {
                    ...preferredOrganization,
                    serviceConsumers,
                };
            })
        );
    }

    function getPropsForFormSection(): IPersonalSettingsCarePathServiceProviderSettingFormSectionProps {
        const propsForFormSection = {
            mode,
            errors,
            handleChange: handleChange,
            validateOne: validateOne,
        };
        if (mode === 'edit' && personalSettingsCarePathServiceProviderSettingEditVersion) {
            return { ...propsForFormSection, ...personalSettingsCarePathServiceProviderSettingEditVersion };
        } else {
            return { ...propsForFormSection, ...props.serviceProviderRight };
        }
    }

    function showEditIcon() {
        return (
            mode === 'read' &&
            props.serviceProviderRight.service.serviceType !== ServiceType.referral &&
            props.serviceProviderRight.service.serviceType !== ServiceType.referralSmall &&
            canEdit
        );
    }

    function handleChange<K extends keyof IServiceProvider>(name: K, value: IServiceProvider[K]) {
        setPersonalSettingsCarePathServiceProviderSettingEditVersion({
            ...personalSettingsCarePathServiceProviderSettingEditVersion,
            [name]: value,
        } as IServiceProvider);
    }

    function validateOne(name: keyof IServiceProvider) {
        setErrors(
            updateValidationErrorsForOne(
                serviceProviderPersonalSettingsEditSchema,
                personalSettingsCarePathServiceProviderSettingEditVersion,
                errors,
                name
            )
        );
    }

    async function handleSubmit() {
        if (personalSettingsCarePathServiceProviderSettingEditVersion) {
            setIsSaving(true);

            const itemToSave = createPersonalSettingsCarePathServiceProviderSettingToSave();

            const { errors: validationErrors, item } = validate(serviceProviderPersonalSettingsEditSchema, itemToSave);

            if (item?.isServiceConsumerSelector && !preferredOrganizationConsumers.length) {
                setNoPreferredServiceConsumerSelected(true);
                setIsSaving(false);

                return;
            }

            if (validationErrors.size > 0) {
                setErrors(validationErrors);
            } else {
                await savePreferredOrganizations();

                const url = `/api/serviceProviders/${props.serviceProviderRight.id}`;
                await fetchJson(url, 'POST', item);

                await props.reloadServiceProviderRight();
                setErrors(emptyValidationErrors);
                setMode('read');
            }
            setIsSaving(false);
        }
    }

    function createPersonalSettingsCarePathServiceProviderSettingToSave() {
        if (personalSettingsCarePathServiceProviderSettingEditVersion) {
            const startDate = props.serviceProviderRight.startDate;
            const isAnalyst =
                personalSettingsCarePathServiceProviderSettingEditVersion.isAnalyst &&
                props.serviceProviderRight.service.code === ServiceCode.TCOH;
            const isBiometrician = isAnalyst
                ? false
                : personalSettingsCarePathServiceProviderSettingEditVersion.isBiometrician;
            const isGrader = isAnalyst ? false : personalSettingsCarePathServiceProviderSettingEditVersion.isGrader;
            const isRequestCreator = isBiometrician
                ? personalSettingsCarePathServiceProviderSettingEditVersion.isRequestCreator
                : false;
            const isGraderSelector = isBiometrician
                ? personalSettingsCarePathServiceProviderSettingEditVersion.isGraderSelector
                : false;
            const isServiceConsumerSelector = isRequestCreator
                ? personalSettingsCarePathServiceProviderSettingEditVersion.isServiceConsumerSelector
                : false;
            const canSelectCareProviderAsServiceConsumer = isServiceConsumerSelector
                ? personalSettingsCarePathServiceProviderSettingEditVersion.canSelectCareProviderAsServiceConsumer
                : false;
            const isRequestRecipient = personalSettingsCarePathServiceProviderSettingEditVersion.isRequestRecipient;
            const usingSpirometer =
                personalSettingsCarePathServiceProviderSettingEditVersion.usingSpirometer &&
                props.serviceProviderRight.service.code === ServiceCode.TPO;
            const spirometerSoftwareName = usingSpirometer
                ? personalSettingsCarePathServiceProviderSettingEditVersion.spirometerSoftwareName
                : null;
            const usingRestEcgMeter =
                personalSettingsCarePathServiceProviderSettingEditVersion.usingRestEcgMeter &&
                props.serviceProviderRight.service.code === ServiceCode.TCOR;

            return {
                startDate,
                isAnalyst,
                isBiometrician,
                isGrader,
                isRequestCreator,
                isGraderSelector,
                isServiceConsumerSelector,
                canSelectCareProviderAsServiceConsumer,
                isRequestRecipient,
                usingSpirometer,
                spirometerSoftwareName,
                usingRestEcgMeter,
                isAutoCreated: personalSettingsCarePathServiceProviderSettingEditVersion.isAutoCreated,
            };
        }

        return {};
    }

    async function savePreferredOrganizations() {
        const originalPreferredOrganizationIds = originalPreferredOrganizations.map(
            (organization) => organization.organization.id
        );
        const selectedPreferredOrganizationIds = preferredOrganizationConsumers.map(
            (preferredOrganizationConsumer) => preferredOrganizationConsumer.organization.id
        );

        const deletedServiceOrganizationConsumerIds = originalPreferredOrganizations
            .filter((organization) => !selectedPreferredOrganizationIds.includes(organization.organization.id))
            .map((serviceOrganization) => serviceOrganization.id);

        if (deletedServiceOrganizationConsumerIds.length) {
            await abortableFetch.fetchJson('/api/preferredServiceOrganizationConsumers', 'DELETE', {
                preferredServiceOrganizationConsumerIds: deletedServiceOrganizationConsumerIds,
            });
        }

        const addedOrganizationIds = difference(selectedPreferredOrganizationIds, originalPreferredOrganizationIds);

        if (addedOrganizationIds.length) {
            await Promise.all(
                addedOrganizationIds.map(async (addedOrganizationId) => {
                    await abortableFetch.fetchJson('/api/preferredServiceOrganizationConsumers', 'POST', {
                        serviceProviderId: props.serviceProviderRight.id,
                        organizationId: addedOrganizationId,
                    });
                })
            );
        }
    }
}
